// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ChevronDown, Loader2, RotateCcw, Save } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SLACK_USERNAME_SETTING, useProfileQuery, useUpdateProfileSettingsMutation } from "@/api/hooks/use-profile";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { DATE_FORMAT_OPTIONS, DATE_FORMAT_SAMPLE_DATE, formatDate } from "@/lib/format";
import {
  ACCENT_OPTIONS,
  PAGE_SIZE_OPTIONS,
  resetLocalPreferences,
  useAccent,
  useDateFormat,
  useItemsPerPage,
  useShowHttpErrorToasts,
} from "@/lib/preferences";
import { type ThemeChoice, useThemeSwitch } from "@/lib/theme-transition";
import { cn } from "@/lib/utils";

const THEMES = ["light", "dark", "system"] as const;

/* Swatches are literal, not `bg-primary`: the picker has to show all three accents at once,
   while the CSS variables only ever hold the active one. Keep in sync with the
   `[data-accent]` palettes in globals.css. */
const ACCENT_SWATCHES: Record<(typeof ACCENT_OPTIONS)[number], string> = {
  green: "bg-[oklch(0.612_0.146_157)]",
  red: "bg-[oklch(0.575_0.12_22)]",
  blue: "bg-[oklch(0.615_0.145_245)]",
};

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
] as const;

export function ProfileSettingsTab() {
  return (
    <div className="max-w-2xl space-y-6 p-6">
      <AppearanceSection />
      <Separator />
      <TablesSection />
      <Separator />
      <IntegrationsSection />
      <Separator />
      <AdvancedSection />
    </div>
  );
}

function AppearanceSection() {
  const { t, i18n } = useTranslation("back-office");
  const { theme } = useTheme();
  const switchTheme = useThemeSwitch();
  const themeTriggerRef = useRef<HTMLButtonElement>(null);
  const [dateFormat, setDateFormat] = useDateFormat();
  const [accent, setAccent] = useAccent();
  const [mounted, setMounted] = useState(false);

  useEffect(function markMounted() {
    setMounted(true);
  }, []);

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.settings.appearance.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.settings.appearance.description")}</p>
      </div>
      {/* Light/dark and accent pair up on the first row — they're the two independent axes of
          the same paint job; language and date format share the second so the
          "language ≠ date format" distinction reads as a deliberate pairing. */}
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("profile.settings.theme.label")}</Label>
          <Select
            value={mounted ? (theme ?? "system") : "system"}
            onValueChange={(value) => switchTheme(value as ThemeChoice, themeTriggerRef.current)}
          >
            <SelectTrigger ref={themeTriggerRef} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THEMES.map((th) => (
                <SelectItem key={th} value={th}>
                  {t(`commandPalette.theme.${th}`, th)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("profile.settings.accent.label")}</Label>
          <Select value={accent} onValueChange={setAccent}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCENT_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  <span className={cn("size-3 shrink-0 rounded-full", ACCENT_SWATCHES[option])} />
                  {t(`profile.settings.accent.options.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("profile.settings.language.label")}</Label>
          <Select value={i18n.resolvedLanguage ?? "en"} onValueChange={(lng) => i18n.changeLanguage(lng)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  <span className="text-base leading-none">{lang.flag}</span>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t("profile.settings.language.hint")}</p>
        </div>
        <div className="space-y-2">
          <Label>{t("profile.settings.dateFormat.label")}</Label>
          {/* Rendered only after mount: the "system" sample depends on the host locale, which would
              differ from a server-rendered guess and trip a hydration mismatch. */}
          <Select value={dateFormat} onValueChange={setDateFormat}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_FORMAT_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`profile.settings.dateFormat.options.${option}`)}
                  <span className="font-mono text-xs text-muted-foreground">
                    {mounted ? formatDate(DATE_FORMAT_SAMPLE_DATE, option) : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t("profile.settings.dateFormat.hint")}</p>
        </div>
      </div>
    </section>
  );
}

function TablesSection() {
  const { t } = useTranslation("back-office");
  const [itemsPerPage, setItemsPerPage] = useItemsPerPage();

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.settings.tables.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.settings.tables.description")}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("profile.settings.itemsPerPage.label")}</Label>
          <Select value={String(itemsPerPage)} onValueChange={(v) => setItemsPerPage(Number(v))}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {t("dataTable.rows", { count: size })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t("profile.settings.itemsPerPage.hint")}</p>
        </div>
      </div>
    </section>
  );
}

function IntegrationsSection() {
  const { t } = useTranslation("back-office");
  const { data: savedHandle } = useProfileQuery((p) => p.settings[SLACK_USERNAME_SETTING] ?? "");
  const updateSettings = useUpdateProfileSettingsMutation();
  const [handle, setHandle] = useState("");

  // Seed the input from the loaded profile (and re-sync after a successful save refetch).
  useEffect(
    function syncHandleFromProfile() {
      setHandle(savedHandle ?? "");
    },
    [savedHandle],
  );

  async function onSave() {
    try {
      await updateSettings.mutateAsync({ [SLACK_USERNAME_SETTING]: handle.trim() });
      toast.success(t("profile.settings.slack.saved"));
    } catch {
      // The global API-error toast is opt-in (off by default) and doesn't cover 400/403, so a failed
      // save would otherwise be silent and look like it succeeded. Always surface it here. CIN-676.
      toast.error(t("profile.settings.slack.saveError"));
    }
  }

  const unchanged = handle.trim() === (savedHandle ?? "");

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.settings.integrations.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.settings.integrations.description")}</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="slack-handle">{t("profile.settings.slack.label")}</Label>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <Input
            id="slack-handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder={t("profile.settings.slack.placeholder")}
            disabled={updateSettings.isPending}
          />
          <Button type="submit" disabled={unchanged || updateSettings.isPending} className="shrink-0 gap-2">
            {updateSettings.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("profile.settings.slack.save")}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground">{t("profile.settings.slack.hint")}</p>
      </div>
    </section>
  );
}

function AdvancedSection() {
  const { t } = useTranslation("back-office");

  return (
    <Collapsible className="space-y-4">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 text-left">
        <div>
          <h3 className="text-sm font-medium">{t("profile.settings.advanced.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("profile.settings.advanced.description")}</p>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-6 border-l border-border pl-4 pt-2">
        <DiagnosticsSection />
        <Separator />
        <ResetSection />
      </CollapsibleContent>
    </Collapsible>
  );
}

function DiagnosticsSection() {
  const { t } = useTranslation("back-office");
  const [httpErrorToasts, setHttpErrorToasts] = useShowHttpErrorToasts();

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.settings.diagnostics.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.settings.diagnostics.description")}</p>
      </div>
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="http-error-toasts">{t("profile.settings.httpErrorToasts.label")}</Label>
          <p className="text-xs text-muted-foreground">{t("profile.settings.httpErrorToasts.hint")}</p>
        </div>
        <Switch
          id="http-error-toasts"
          checked={httpErrorToasts}
          onCheckedChange={setHttpErrorToasts}
          className="shrink-0"
        />
      </div>
    </section>
  );
}

function ResetSection() {
  const { t } = useTranslation("back-office");
  const [open, setOpen] = useState(false);

  function onConfirm() {
    resetLocalPreferences();
    toast.success(t("profile.settings.reset.toast"));
    // Reload so theme/language/i18n re-initialise from their defaults.
    window.location.reload();
  }

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.settings.reset.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.settings.reset.description")}</p>
      </div>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <RotateCcw className="h-4 w-4" />
        {t("profile.settings.reset.button")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          id="dialog-reset-preferences"
          className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
        >
          <div className="flex flex-row">
            <WizardPane variant="destructive" icon={RotateCcw} />
            <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
              <DialogHeader>
                <DialogTitle>{t("profile.settings.reset.dialog.title")}</DialogTitle>
                <DialogDescription>{t("profile.settings.reset.dialog.description")}</DialogDescription>
              </DialogHeader>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("profile.settings.reset.dialog.cancel")}
            </Button>
            <Button variant="destructive" onClick={onConfirm}>
              <RotateCcw className="h-4 w-4" />
              {t("profile.settings.reset.dialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
